import { listingCategoryHref } from '@/constants/module-url-prefix';
import { type ListingCardProps } from '@/types';
import { Link } from '@inertiajs/react';
import { MapPin, Star } from 'lucide-react';
import React from 'react';

export const ListingCard: React.FC<ListingCardProps> = ({
    id: _id,
    title,
    slug: _slug,
    href,
    category,
    categoryFriendlyUrlPath,
    location,
    averageRating,
    reviewCount,
    summary,
    imageUrl,
    isFavorited: _isFavorited = false,
    isVerified = false,
    isFeatured = false,
    services = [],
    opensWeekend = false,
    uniformHeight = false,
    onFavorite: _onFavorite,
    onViewProfile,
}) => {
    const hasCategory = Boolean(category?.trim());
    const hasLocation = Boolean(location?.trim());
    const summaryText = summary?.trim() ?? '';

    const viewProfileClassName =
        'mt-auto w-full rounded-lg bg-blue-700 py-2 text-center text-sm font-semibold text-white transition-colors hover:cursor-pointer hover:bg-blue-800';

    // Sem caminho completo não há link válido: exibe a categoria sem href, em vez de gerar uma URL quebrada.
    const categoryHref = listingCategoryHref(categoryFriendlyUrlPath);
    const categoryClassName = uniformHeight
        ? 'mb-0.5 line-clamp-2 block min-h-[2lh] max-w-full py-1 text-xs leading-tight font-bold text-cyan-500 transition-colors'
        : 'mb-0.5 inline-flex w-fit items-center py-1 text-xs font-bold text-cyan-500 transition-colors';

    // Sem categoria, o espaço vazio só é reservado no modo de altura uniforme, para alinhar os cards da grade.
    const renderCategory = () => {
        if (!hasCategory) {
            return uniformHeight ? <span className={categoryClassName}>{'\u00A0'}</span> : null;
        }

        if (!categoryHref) {
            return <span className={categoryClassName}>{category}</span>;
        }

        return (
            <a href={categoryHref} onClick={(e) => e.stopPropagation()} className={categoryClassName}>
                {category}
            </a>
        );
    };

    return (
        <div className="group relative flex h-full w-full flex-col overflow-hidden rounded-lg bg-white shadow-sm transition-all hover:shadow-md">
            {/* Header with Logo */}
            <div className="relative flex h-36 items-center justify-center bg-gray-200 p-6">
                {href ? (
                    // Fora da ordem de tabulação: o título e o "Ver perfil" já levam ao mesmo perfil.
                    <Link href={href} tabIndex={-1} aria-hidden="true" className="flex h-full w-full items-center justify-center">
                        {imageUrl ? (
                            <img src={imageUrl} alt={title} className="max-h-full max-w-full object-contain" />
                        ) : (
                            <div className="text-3xl font-bold text-white">{title.charAt(0)}</div>
                        )}
                    </Link>
                ) : imageUrl ? (
                    <img src={imageUrl} alt={title} className="max-h-full max-w-full object-contain" />
                ) : (
                    <div className="text-3xl font-bold text-white">{title.charAt(0)}</div>
                )}

                {/* Badges */}
                {(isFeatured || isVerified) && (
                    <div className="absolute top-3 left-3 flex flex-row items-center justify-center gap-2">
                        {isFeatured && (
                            <span title="Empresa em destaque" aria-label="Empresa em destaque" className="flex">
                                <Star className="h-3 w-3 fill-current text-yellow-400" aria-hidden="true" />
                            </span>
                        )}
                        {isVerified && (
                            <span
                                title="Empresa verificada pela Compre Náutica"
                                aria-label="Empresa verificada pela Compre Náutica"
                                className="flex size-4 items-center justify-center rounded-full bg-blue-700 text-xs font-semibold text-white"
                            >
                                ✓
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col px-4 pt-2 pb-4">
                {/* Title */}
                <h3
                    className={
                        uniformHeight
                            ? 'line-clamp-2 min-h-[2lh] text-lg leading-snug font-semibold text-gray-900'
                            : 'line-clamp-1 text-lg font-semibold text-gray-900'
                    }
                >
                    {href ? <Link href={href}>{title}</Link> : title}
                </h3>

                {/* Category Badge */}
                {renderCategory()}

                {/* Location */}
                {(uniformHeight || hasLocation) && (
                    <div className="mb-2 flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="h-4 w-4 shrink-0" />
                        <span className="line-clamp-1">{hasLocation ? location : '-'}</span>
                    </div>
                )}

                {/* Rating */}
                <div className="mb-3 flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className={`h-3 w-3 ${star <= averageRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                    </div>
                    {reviewCount > 0 && (
                        <span className="text-xs text-gray-600">
                            {averageRating.toFixed(1)} ({reviewCount} avaliações)
                        </span>
                    )}
                </div>

                {/* CN-200 (5.1): service tags and "abre fim de semana" */}
                {(services.length > 0 || opensWeekend) && (
                    <ul className="mb-3 flex flex-wrap gap-1.5" aria-label="Serviços">
                        {opensWeekend && (
                            <li className="rounded-full bg-[#0f766e]/10 px-2 py-0.5 text-[11px] font-medium text-[#0f766e]">Abre fim de semana</li>
                        )}
                        {services.slice(0, 3).map((service) => (
                            <li
                                key={service}
                                className="max-w-full truncate rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700"
                                title={service}
                            >
                                {service}
                            </li>
                        ))}
                        {services.length > 3 && <li className="px-1 py-0.5 text-[11px] text-gray-500">+{services.length - 3}</li>}
                    </ul>
                )}

                {/* Summary */}
                {uniformHeight ? (
                    <p className="mb-4 line-clamp-2 min-h-[2lh] text-sm text-gray-600">{summaryText || '\u00A0'}</p>
                ) : (
                    summary && <p className="mb-4 line-clamp-2 h-[2lh] text-sm text-gray-600">{summary}</p>
                )}

                {/* View Profile Button */}
                {href ? (
                    <Link href={href} className={viewProfileClassName}>
                        Ver perfil
                    </Link>
                ) : (
                    <button onClick={() => onViewProfile?.()} className={viewProfileClassName}>
                        Ver perfil
                    </button>
                )}
            </div>
        </div>
    );
};
